import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../App.css';
import {
    Container,
    CssBaseline,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    TextField,
    Box,
    Typography,
    Chip,
    Stack,
    CircularProgress,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    IconButton,
    InputAdornment
} from '@mui/material';
import { Add, Edit, Delete, ArrowUpward, ArrowDownward, Search, Clear } from '@mui/icons-material';
import configureAmplify from '../configuration/amplify';
import { Amplify } from 'aws-amplify';
import { AuthSession, fetchAuthSession } from 'aws-amplify/auth';
Amplify.configure(configureAmplify());

type GameSummary = {
    gameid: string;
    firstname: string;
    middlename?: string;
    lastname: string;
    ready: boolean;
}

type SortColumn = 'name' | 'ready';
type SortDirection = 'asc' | 'desc';

const ManageGames: React.FC = () => {
    const navigate = useNavigate();
    const [gameList, setGameList] = useState<GameSummary[]>([]);
    const [filteredGames, setFilteredGames] = useState<GameSummary[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [readyFilter, setReadyFilter] = useState<string>('all');
    const [sortColumn, setSortColumn] = useState<SortColumn>('name');
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

    useEffect(() => {
        loadGames();
    }, []);

    useEffect(() => {
        filterAndSortGames();
    }, [gameList, searchQuery, readyFilter, sortColumn, sortDirection]);

    const loadGames = async () => {
        try {
            setLoading(true);
            const session = await fetchAuthSession();
            const token = session.tokens?.idToken?.toString();

            if (!token) {
                console.error('No authentication token available');
                setLoading(false);
                return;
            }

            const response = await fetch('https://api.admin.famousinitials.com/games', {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': token
                }
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Failed to load games');
            }

            setGameList(data);
        } catch (error) {
            console.error('Error loading games:', error);
            alert('Failed to load games. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const filterAndSortGames = () => {
        let filtered = [...gameList];

        // Apply search filter
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(game => {
                const fullName = `${game.firstname} ${game.middlename || ''} ${game.lastname}`.toLowerCase();
                return fullName.includes(query);
            });
        }

        // Apply ready filter
        if (readyFilter === 'ready') {
            filtered = filtered.filter(game => game.ready);
        } else if (readyFilter === 'not-ready') {
            filtered = filtered.filter(game => !game.ready);
        }

        // Apply sorting
        filtered.sort((a, b) => {
            let compareValue = 0;

            if (sortColumn === 'name') {
                const nameA = getFullName(a).toLowerCase();
                const nameB = getFullName(b).toLowerCase();
                compareValue = nameA.localeCompare(nameB);
            } else if (sortColumn === 'ready') {
                // Ready games first when ascending, not ready first when descending
                compareValue = (a.ready === b.ready) ? 0 : a.ready ? -1 : 1;
            }

            return sortDirection === 'asc' ? compareValue : -compareValue;
        });

        setFilteredGames(filtered);
    };

    const handleSort = (column: SortColumn) => {
        if (sortColumn === column) {
            // Toggle direction if same column
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            // New column, default to ascending
            setSortColumn(column);
            setSortDirection('asc');
        }
    };

    const deleteGame = async (id: string, name: string) => {
        if (!confirm(`Are you sure you want to delete ${name}?`)) {
            return;
        }

        try {
            const session = await fetchAuthSession();
            const token = session.tokens?.idToken?.toString();

            if (!token) {
                console.error('No authentication token available');
                return;
            }

            const response = await fetch(`https://api.admin.famousinitials.com/games/${id}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': token
                }
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Failed to delete game');
            }

            setGameList(gameList.filter(game => game.gameid !== id));
            alert('Game deleted successfully');
        } catch (error) {
            console.error('Error deleting game:', error);
            alert('Failed to delete game. Please try again.');
        }
    };

    const getFullName = (game: GameSummary) => {
        return `${game.firstname} ${game.middlename ? game.middlename + ' ' : ''}${game.lastname}`;
    };

    return (
        <React.Fragment>
            <CssBaseline />
            <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
                <Box sx={{ mb: 3 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                        <Typography variant="h4">Manage Games</Typography>
                        <Button
                            variant="contained"
                            startIcon={<Add />}
                            onClick={() => navigate('/games/new')}
                        >
                            Create New Game
                        </Button>
                    </Stack>

                    <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                        <TextField
                            label="Search by name"
                            variant="outlined"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            fullWidth
                            placeholder="Type to search..."
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Search />
                                    </InputAdornment>
                                ),
                                endAdornment: searchQuery && (
                                    <InputAdornment position="end">
                                        <IconButton
                                            size="small"
                                            onClick={() => setSearchQuery('')}
                                            edge="end"
                                        >
                                            <Clear />
                                        </IconButton>
                                    </InputAdornment>
                                )
                            }}
                        />
                        <FormControl sx={{ minWidth: 200 }}>
                            <InputLabel>Status Filter</InputLabel>
                            <Select
                                value={readyFilter}
                                label="Status Filter"
                                onChange={(e) => setReadyFilter(e.target.value)}
                            >
                                <MenuItem value="all">All Games</MenuItem>
                                <MenuItem value="ready">Ready Only</MenuItem>
                                <MenuItem value="not-ready">Not Ready Only</MenuItem>
                            </Select>
                        </FormControl>
                    </Stack>

                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        💡 Click column headers to sort the table
                    </Typography>
                </Box>

                {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                        <CircularProgress />
                    </Box>
                ) : (
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                cursor: 'pointer',
                                                userSelect: 'none',
                                                '&:hover': { opacity: 0.7 }
                                            }}
                                            onClick={() => handleSort('name')}
                                        >
                                            <strong>Name</strong>
                                            {sortColumn === 'name' && (
                                                sortDirection === 'asc' ?
                                                    <ArrowUpward fontSize="small" sx={{ ml: 0.5 }} /> :
                                                    <ArrowDownward fontSize="small" sx={{ ml: 0.5 }} />
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                cursor: 'pointer',
                                                userSelect: 'none',
                                                '&:hover': { opacity: 0.7 }
                                            }}
                                            onClick={() => handleSort('ready')}
                                        >
                                            <strong>Status</strong>
                                            {sortColumn === 'ready' && (
                                                sortDirection === 'asc' ?
                                                    <ArrowUpward fontSize="small" sx={{ ml: 0.5 }} /> :
                                                    <ArrowDownward fontSize="small" sx={{ ml: 0.5 }} />
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell align="right"><strong>Actions</strong></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredGames.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={3} align="center">
                                            <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                                                {gameList.length === 0
                                                    ? 'No games yet. Create your first game!'
                                                    : 'No games match your search criteria.'}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    filteredGames.map((game) => (
                                        <TableRow key={game.gameid} hover>
                                            <TableCell>{getFullName(game)}</TableCell>
                                            <TableCell align="center">
                                                <Chip
                                                    label={game.ready ? 'Ready' : 'Not Ready'}
                                                    color={game.ready ? 'success' : 'default'}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell align="right">
                                                <IconButton
                                                    color="primary"
                                                    onClick={() => navigate(`/games/${game.gameid}`)}
                                                    title="Edit"
                                                >
                                                    <Edit />
                                                </IconButton>
                                                <IconButton
                                                    color="error"
                                                    onClick={() => deleteGame(game.gameid, getFullName(game))}
                                                    title="Delete"
                                                >
                                                    <Delete />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                {!loading && filteredGames.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="text.secondary">
                            Showing {filteredGames.length} of {gameList.length} games
                        </Typography>
                    </Box>
                )}
            </Container>
        </React.Fragment>
    );
}

export default ManageGames;

